/**
 * Mirrors: spherical (concave, convex) and plane.
 *
 * A spherical mirror is the ideal element folded back (engine target §4.3):
 * f = R/2, converging when concave. A plane mirror is the same element with
 * f = ∞ — which is an exact reflection, so the formula covers it (v = −u) and
 * its angles of incidence are real ones.
 *
 * `angle` is the side that reflects. The back absorbs.
 */
import { PartKind, numbered } from '../registry';
import { box, heights, localToWorld, sag } from '../shapes';
import { Fact, Part, Surface, ThinElement, Vec2, World } from '../types';
import { fromAngle, toWorld } from '../vec';

export interface SphericalMirrorData {
    curve: 'concave' | 'convex';
    /** Radius of curvature, cm, a magnitude. */
    radius: number;
    aperture: number;
    [key: string]: unknown;
}

export interface PlaneMirrorData {
    length: number;
    [key: string]: unknown;
}

/** Engine focal length: positive converges (concave). Not the New Cartesian one. */
export const mirrorF = (d: SphericalMirrorData): number =>
    (d.curve === 'concave' ? 1 : -1) * (d.radius / 2);

const BACK = 0.5;

/** Which way the reflecting side faces, in words. The back is hatched in the drawing. */
export function facing(angle: number): string {
    const c = Math.cos(angle), sn = Math.sin(angle);
    if (Math.abs(c) >= Math.abs(sn)) return c < 0 ? 'faces left' : 'faces right';
    return sn > 0 ? 'faces up' : 'faces down';
}

function idealPlane(p: Part, w: World, half: number, f: number, name: string): Surface {
    return {
        id: `${p.id}:face`, partId: p.id,
        shape: { form: 'segment', a: toWorld(p.at, p.angle, { x: 0, y: -half }), b: toWorld(p.at, p.angle, { x: 0, y: half }) },
        rule: { law: 'ideal', f, folds: true, centre: p.at, axis: fromAngle(p.angle) },
        name,
    };
}

export const sphericalMirror: PartKind<SphericalMirrorData> = {
    key: 'sphericalMirror',
    label: 'Spherical mirror',
    tag: 'M',
    freedom: { onAxis: true, turns: true },

    // Faces back along the rail, towards where the object usually is.
    create: (at: Vec2, id: string): Part<SphericalMirrorData> => ({
        id, kind: 'sphericalMirror', at: { x: at.x, y: 0 }, angle: Math.PI, mount: 'axis',
        data: { curve: 'concave', radius: 30, aperture: 14 },
    }),

    element: (p): ThinElement => ({
        partId: p.id, centre: p.at, axis: fromAngle(p.angle),
        f: mirrorF(p.data), folds: true, halfAperture: p.data.aperture / 2,
    }),

    surfaces: (p, w) => [idealPlane(p as Part, w, p.data.aperture / 2, mirrorF(p.data), numbered(p as Part, w, 'M'))],

    outline(p) {
        // Concave curves towards the side it faces; convex away from it.
        const s = p.data.curve === 'concave' ? 1 : -1;
        const ys = heights(p.data.aperture / 2);
        const face = ys.map(y => ({ x: s * sag(p.data.radius, y), y }));
        const back = [...ys].reverse().map(y => ({ x: s * sag(p.data.radius, y) - BACK, y }));
        return localToWorld(p as Part, [...face, ...back]);
    },

    controls: () => [
        {
            type: 'choice', key: 'curve', label: 'Curve',
            choices: [{ value: 'concave', label: 'concave' }, { value: 'convex', label: 'convex' }],
        },
        { type: 'number', key: 'radius', label: 'Radius of curvature R', unit: 'cm', min: 5, max: 200, step: 0.5 },
        { type: 'number', key: 'aperture', label: 'Aperture', unit: 'cm', min: 2, max: 40, step: 0.5 },
    ],

    keyFacts(p): Fact[] {
        const f = mirrorF(p.data);
        const concave = p.data.curve === 'concave';
        return [
            { label: 'Reflecting side', value: facing(p.angle) },
            { label: 'Focal length', value: `${Math.abs(f).toFixed(1)} cm, ${concave ? 'converging' : 'diverging'}` },
            // New Cartesian, for light arriving at the reflecting face: a concave
            // mirror's focus is on the side the light came from, so f is negative.
            { label: 'In the sign convention', value: `f = ${concave ? '−' : '+'}${Math.abs(f).toFixed(1)} cm, R = ${concave ? '−' : '+'}${p.data.radius.toFixed(1)} cm` },
        ];
    },

    name: (p, w) => numbered(p as Part, w, 'M'),
};

export const planeMirror: PartKind<PlaneMirrorData> = {
    key: 'planeMirror',
    label: 'Plane mirror',
    tag: 'M',
    freedom: { onAxis: true, turns: true },

    create: (at: Vec2, id: string): Part<PlaneMirrorData> => ({
        id, kind: 'planeMirror', at, angle: Math.PI, mount: 'free', data: { length: 10 },
    }),

    element: (p): ThinElement => ({
        partId: p.id, centre: p.at, axis: fromAngle(p.angle),
        f: Infinity, folds: true, halfAperture: p.data.length / 2,
    }),

    surfaces: (p, w) => [idealPlane(p as Part, w, p.data.length / 2, Infinity, numbered(p as Part, w, 'M'))],

    outline: (p) => localToWorld(p as Part, box(-BACK / 2, BACK / 2, p.data.length / 2)),

    controls: () => [
        { type: 'number', key: 'length', label: 'Length', unit: 'cm', min: 2, max: 60, step: 0.5 },
    ],

    keyFacts: (p): Fact[] => [{ label: 'Reflecting side', value: facing(p.angle) }],

    name: (p, w) => numbered(p as Part, w, 'M'),
};
